import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router'
import { Toaster } from 'sonner'
import React from 'react'

import { useSessionStore } from './store/auth/auth.store'
import { Login } from './pages/Login'

import { Layout } from './components/Layout'
import { GlobalTooltip } from './components/ui/GlobalTooltip'
import { Clients } from './pages/Clients'
import { Dashboard } from './pages/Dashboard'
import { Inventory } from './pages/Inventory'
import { InventoryMovements } from './pages/InventoryMovements'
import { Loyalty } from './pages/Loyalty'
import { MultiBranch } from './pages/MultiBranch'
import { PointOfSale } from './pages/PointOfSale'
import { Purchases } from './pages/Purchases'
import { Regulatory } from './pages/Regulatory'
import { Reports } from './pages/Reports'
import { Sales } from './pages/Sales'
import { Users } from './pages/Users'
import { Sucursals } from './pages/Sucursals'
import { Categorias } from './pages/Categorias'
import { LogsAccesos } from './pages/LogsAccesos'
import { LogsAcciones } from './pages/LogsAcciones'
import { Departments } from './pages/Departments'
import { ProductTypes } from './pages/ProductTypes'
import { Transfers } from './pages/Transfers'
import { Taxes } from './pages/Taxes'
import { Units } from './pages/Unit'
// import { Notifications } from './pages/Notifications'
import { PlatformSettings } from './pages/PlatformSettings'
import { Themes } from './pages/Themes'
import { PaymentMethods } from './pages/PaymentMethods'
import { CashRegisters } from './pages/CashRegisters'
import { UseCases } from './pages/UseCases'
import { CashHistory } from './pages/CashHistory'
import { Doctors } from './pages/Doctors'
import { Banks } from './pages/Banks'

// Wrapper para proteger rutas
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useSessionStore((s) => s.token)
  const location = useLocation()

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

function PermissionRoute({
  permission,
  children,
}: {
  permission: string | string[]
  children: React.ReactNode
}) {
  const canAction = useSessionStore((s) => s.canAction)
  const canAccess = Array.isArray(permission)
    ? permission.some((action) => canAction(action))
    : canAction(permission)

  if (!canAccess) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

export default function App() {
  const token = useSessionStore((s) => s.token)

  return (
    <BrowserRouter>
      <Routes>
        {/* Ruta pública / Login */}
        <Route
          path="/login"
          element={
            token ? (
              <Navigate to="/" replace />
            ) : (
              <Login onAuthenticated={() => {
                // Token is set inside Login via authStore, redirect is handled by react-router on re-render
              }} />
            )
          }
        />

        {/* Rutas Protegidas */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/pos" element={<PointOfSale />} />
                  <Route path="/historial-caja" element={<PermissionRoute permission="configuration.cash.view"><CashHistory /></PermissionRoute>} />
                  <Route path="/ventas" element={<Sales />} />
                  <Route path="/catalogo" element={<PermissionRoute permission="inventory.catalog.view"><Inventory /></PermissionRoute>} />
                  <Route path="/inventario/movimientos" element={<PermissionRoute permission="inventory.traceability.view"><InventoryMovements /></PermissionRoute>} />
                  <Route path="/inventario/transferencias" element={<PermissionRoute permission="inventory.catalog-transfer.view"><Transfers /></PermissionRoute>} />
                  <Route path="/compras" element={<PermissionRoute permission={['purchases.purchase.view', 'purchases.supplier.view']}><Purchases /></PermissionRoute>} />
                  <Route path="/reportes" element={<Reports />} />
                  <Route path="/clientes" element={<Clients />} />
                  <Route path="/lealtad" element={<Loyalty />} />
                  <Route path="/usuarios" element={<PermissionRoute permission="users.user.view"><Users /></PermissionRoute>} />
                  <Route path="/regulatorio" element={<Regulatory />} />
                  <Route path="/dashboard-sucursales" element={<MultiBranch />} />
                  <Route path="/sucursales" element={<PermissionRoute permission="sucursals.sucursal.view"><Sucursals /></PermissionRoute>} />
                  <Route path="/categorias" element={<PermissionRoute permission="inventory.category.view"><Categorias /></PermissionRoute>} />
                  <Route path="/logs-accesos" element={<PermissionRoute permission="logs.access-log.view"><LogsAccesos /></PermissionRoute>} />
                  <Route path="/logs-acciones" element={<PermissionRoute permission="logs.activity-log.view"><LogsAcciones /></PermissionRoute>} />
                  <Route path="/departamentos" element={<PermissionRoute permission="inventory.department.view"><Departments /></PermissionRoute>} />
                  <Route path="/tipos-producto" element={<PermissionRoute permission="inventory.product-type.view"><ProductTypes /></PermissionRoute>} />
                  <Route path="/casos-de-uso" element={<PermissionRoute permission="inventory.use-case.view"><UseCases /></PermissionRoute>} />
                  <Route path="/impuestos" element={<PermissionRoute permission="configuration.tax.view"><Taxes /></PermissionRoute>} />
                  <Route path="/unidades-medida" element={<PermissionRoute permission="configuration.unit.view"><Units /></PermissionRoute>} />
                  <Route path="/metodos-pago" element={<PermissionRoute permission="configuration.payment-method.view"><PaymentMethods /></PermissionRoute>} />
                  <Route path="/cash-registers" element={<PermissionRoute permission="configuration.cash.view"><CashRegisters /></PermissionRoute>} />
                  <Route path="/configuracion/plataforma" element={<PermissionRoute permission="configuration.configuration.view"><PlatformSettings /></PermissionRoute>} />
                  <Route path="/configuracion/temas" element={<PermissionRoute permission="configuration.configuration.view"><Themes /></PermissionRoute>} />
                  <Route path="/doctores" element={<Doctors />} />
                  <Route path="/bancos" element={<PermissionRoute permission="configuration.bank.view"><Banks /></PermissionRoute>} />
                  {/* <Route path="/notificaciones" element={<Notifications />} /> */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <GlobalTooltip />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  )
}
